import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  InputAdornment,
  Slider,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import * as React from "react";
import Iconify from "../../../components/Iconify";
import { Link, useNavigate, useParams } from "react-router-dom";
import SyncLoader from "react-spinners/SyncLoader";
import Page from "../../../components/Page";
import { UserMoreMenu } from "../user";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import BatchDetailsCard from "./components/BatchDetailsCard";
import "./style.css";
import { useState, useEffect } from "react";
import { DataGrid, GridToolbar, GridToolbarExport } from "@mui/x-data-grid";

const AccordionStyle = {
  background: "#e6e6ff",
  borderRadius: "18px",
  width: "90%",
  position: "relative",
  transition: "all 0.2s",
  "&:hover": { background: "#ccccff" },
};

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

const BatchDetails = () => {
  const navigate = useNavigate();
  const { Id } = useParams();

  const [users, usersSet] = useState([]);
  const [loading, setLoading] = useState(false);
  const [iid, setiid] = useState();
  const [batchName, setBatchName] = useState("");
  const [courseName, setCourseName] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(5);
  const [page, setPage] = useState(0);

  // role/access
  const detailObject = JSON.parse(localStorage.getItem("detailObject") || "{}");
  const contentAccess = detailObject?.accessToContent;
  const role = localStorage.getItem("role");

  // Audio state
  const audioRefs = React.useRef({});
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState(null);
  const [audioProgress, setAudioProgress] = useState({});
  const [audioDuration, setAudioDuration] = useState({});
  const [isSeeking, setIsSeeking] = useState({});

  const formatTime = (time) => {
    if (!Number.isFinite(time) || time < 0) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    };

  // Fetch lectures
  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

        // Lectures
        const resLect = await fetch(
          `${process.env.REACT_APP_LIVE_URL_NEW}/admin/get-lecture-by-course-id/${Id}`,
          { method: "GET", headers }
        );
        const dataLect = await resLect.json();
        const list = (dataLect?.data || []).filter((l) => l.course === Number(Id));
        usersSet(Array.isArray(list) ? list : []);

        // Course name
        const resCourse = await fetch(
          `${process.env.REACT_APP_LIVE_URL_NEW}/admin/get-course-by-id/${Id}`,
          { method: "GET", headers }
        );
        const dataCourse = await resCourse.json();
        setCourseName(dataCourse?.data?.course?.name || "");
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [Id]);

  // DataGrid helpers
  const handleSelectionChange = (newSelection) => setSelectedRows(newSelection);

  const filteredUsers =
    users?.filter((user) => {
      const titleMatch = user?.title?.toLowerCase?.().includes(searchTerm.toLowerCase());
      const subjectMatch = user?.subject?.name?.toLowerCase?.().includes(searchTerm.toLowerCase());
      return titleMatch || subjectMatch;
    }) || [];

  const startIndex = page * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  const handlePageChange = (_e, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (e) => {
    setPageSize(parseInt(e.target?.value, 10));
    setPage(0);
  };

  const handleDeleteSelected = async () => {
    try {
      const token = localStorage.getItem("token");
      await fetch(`${process.env.REACT_APP_LIVE_URL_NEW}/admin/delete-bulk-lecture`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids: [...selectedRows] }),
      });
      toast.success("Deleted successfully");
      // soft refresh list
      usersSet((prev) => prev.filter((r) => !selectedRows.includes(r.id)));
      setSelectedRows([]);
    } catch (e) {
      console.error(e);
      toast.error("Delete failed");
    }
  };

  // Audio logic (attach handlers inline on <audio/>)
  const onPlayPause = async (lectureId) => {
    const el = audioRefs.current[lectureId];
    if (!el) return;

    // pause any other audio
    if (currentlyPlayingId && currentlyPlayingId !== lectureId) {
      const other = audioRefs.current[currentlyPlayingId];
      if (other && !other.paused) other.pause();
    }

    if (el.paused) {
      try {
        await el.play();
        setCurrentlyPlayingId(lectureId);
      } catch (err) {
        console.error("Error playing audio:", err);
      }
    } else {
      el.pause();
      setCurrentlyPlayingId(null);
    }
  };

  const onAudioTimeUpdate = (lectureId) => {
    if (isSeeking[lectureId]) return;
    const el = audioRefs.current[lectureId];
    if (!el) return;
    setAudioProgress((prev) => ({ ...prev, [lectureId]: el.currentTime }));
  };

  const onAudioLoadedMeta = (lectureId) => {
    const el = audioRefs.current[lectureId];
    setAudioDuration((prev) => ({ ...prev, [lectureId]: el?.duration || 0 }));
    // ensure initial progress = 0 for new mounts
    setAudioProgress((prev) => ({ ...prev, [lectureId]: el?.currentTime || 0 }));
  };

  const onAudioEnded = (lectureId) => {
    const el = audioRefs.current[lectureId];
    if (el) el.currentTime = 0;
    setAudioProgress((prev) => ({ ...prev, [lectureId]: 0 }));
    setIsSeeking((prev) => ({ ...prev, [lectureId]: false }));
    if (currentlyPlayingId === lectureId) setCurrentlyPlayingId(null);
  };

  const onSliderChange = (lectureId, _evt, newValue) => {
    setAudioProgress((prev) => ({ ...prev, [lectureId]: Number(newValue) }));
  };

  const onSliderCommit = (lectureId, _evt, newValue) => {
    const el = audioRefs.current[lectureId];
    if (!el) return;
    setIsSeeking((prev) => ({ ...prev, [lectureId]: true }));
    el.currentTime = Number(newValue);
    setIsSeeking((prev) => ({ ...prev, [lectureId]: false }));
  };

  const CustomExportToolbar = () => (
    <div>
      <GridToolbarExport />
    </div>
  );

  const columns = [
    { field: "title", headerName: "Lecture Name", width: 220 },
    {
      field: "subject",
      headerName: "Subject",
      width: 200,
      valueGetter: ({ value }) => value?.name || "NA",
    },
    {
      field: "material_type",
      headerName: "Material",
      width: 120,
      valueGetter: ({ value }) => (value === "vi" ? "Video" : value === "au" ? "Audio" : "Notes"),
    },
    {
      field: "lecture_type",
      headerName: "Lecture Type",
      width: 140,
      valueGetter: (params) => {
        const t = params.row.lecture_type;
        return t === "1" ? "Live" : t === "2" ? "Recorded" : "NA";
      },
    },
    {
      field: "actions",
      headerName: "View",
      width: 300,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        const isVideo = row?.material_type === "vi";
        const isLive = row?.lecture_type === "1";
        const lectureId = row.id;

        if (isVideo) {
          return (
            <Box onClick={(e) => e.stopPropagation()}>
              <a
                href={`/dashboard/youtubeLive/${row.id}/${row.title}/${"<p>description</p>"}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none" }}
              >
                <Button variant="contained" color="primary" sx={{ width: 100 }}>
                  {isLive ? "Live" : "View"}
                </Button>
              </a>
            </Box>
          );
        }

        if (row.material_type === "au") {
          // AUDIO CELL
          const el = audioRefs.current[lectureId];
          const duration = Number.isFinite(audioDuration[lectureId]) ? audioDuration[lectureId] : 0;
          const position = Number.isFinite(audioProgress[lectureId]) ? audioProgress[lectureId] : 0;
          const canSeek = duration > 0;
          const displayPos = clamp(position, 0, duration || 0);
          const isThisPlaying = currentlyPlayingId === lectureId;

          return (
            <Box
              sx={{
                width: "100%",
                maxWidth: 320,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <audio
                ref={(node) => {
                  if (node) {
                    audioRefs.current[lectureId] = node;
                  }
                }}
                src={row?.material_url?.replace(/\\/g, "")}
                preload="metadata"
                onEnded={() => onAudioEnded(lectureId)}
                onTimeUpdate={() => onAudioTimeUpdate(lectureId)}
                onLoadedMetadata={() => onAudioLoadedMeta(lectureId)}
              />
              <Button
                variant="contained"
                color="primary"
                sx={{ width: 100 }}
                onClick={() => onPlayPause(lectureId)}
              >
                {isThisPlaying && el && !el.paused ? "Pause" : "Play"}
              </Button>

              <Box sx={{ flex: 1 }}>
                <Slider
                  value={displayPos}
                  min={0}
                  max={duration || 0}
                  step={1}
                  disabled={!canSeek}
                  onChange={(evt, v) => onSliderChange(lectureId, evt, v)}
                  onChangeCommitted={(evt, v) => onSliderCommit(lectureId, evt, v)}
                  sx={{ "& .MuiSlider-thumb": { width: 12, height: 12 } }}
                />
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.25 }}>
                  <Typography variant="caption" color="text.secondary">
                    {formatTime(displayPos)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatTime(duration)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        } else {
          // NOTES
          return (
            <Box onClick={(e) => e.stopPropagation()}>
              <a
                href={row.material_url?.replace(/\\/g, "")}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none" }}
              >
                <Button variant="contained" color="primary" sx={{ width: 100 }}>
                  View
                </Button>
              </a>
            </Box>
          );
        }
      },
    },
    {
      field: "action",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box onClick={(e) => e.stopPropagation()}>
          <UserMoreMenu
            title="BatchDetails"
            lectId={params?.id}
            id={params?.id}
            req="delete-lecture"
            batchId={Id}
            setLoading={setLoading}
          />
        </Box>
      ),
    },
  ];

  return (
    <>
      {loading && !users?.length ? (
        <Stack justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
          <SyncLoader color="#8080ff" loading={loading} size={20} />
        </Stack>
      ) : (
        <Page title="Lecture">
          <Grid container spacing={1} sx={{ height: "80vh" }}>
            <Grid item xs={12} sm={12} md={8}>
              <Box spacing={3} ml={5}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={3}
                  sx={{ width: "90%" }}
                >
                  <Typography className="page-heading">
                    All Lectures {courseName ? `(${courseName})` : ""}
                  </Typography>

                  {(contentAccess?.includes?.("Add Center Lecture") || role === "Admin") && (
                    <Button
                      variant="contained"
                      sx={{
                        width: "20%",
                        alignSelf: "center",
                        background: "#8080ff",
                        color: "white",
                        "&:hover": { background: "#0000ff" },
                        textWrap: "nowrap",
                      }}
                      onClick={() => {
                        navigate(`/dashboard/batchDetails/addLecture/${Id}/${batchName}`);
                      }}
                    >
                      Add Lecture
                    </Button>
                  )}
                </Stack>

                {/* Admin table */}
                {role === "Admin" && (
                  <Stack sx={{ width: "100%", borderRadius: "10px" }}>
                    <>
                      {/* Search */}
                      <Box sx={{ mb: 2, ml: 3, mr: 3 }}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          placeholder="Search by Lecture Name or Subject..."
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(0);
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Iconify icon="eva:search-fill" />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#f8f9fa",
                              "&:hover": { backgroundColor: "#e9ecef" },
                              "&.Mui-focused": { backgroundColor: "#ffffff" },
                            },
                          }}
                        />
                      </Box>

                      {filteredUsers.length === 0 ? (
                        <Typography variant="h4" sx={{ textAlign: "center", mt: 4 }}>
                          {searchTerm
                            ? `No lectures found matching "${searchTerm}"`
                            : "No Lecture"}
                        </Typography>
                      ) : (
                        <>
                          <DataGrid
                            rows={paginatedUsers}
                            columns={columns}
                            hideFooterPagination
                            page={page}
                            onPageChange={handlePageChange}
                            style={{
                              marginLeft: "30px",
                              marginTop: "10px",
                              marginBottom: "30px",
                              border: "none",
                            }}
                            checkboxSelection
                            onRowSelectionModelChange={(newSelection) => {
                              handleSelectionChange(newSelection);
                            }}
                            rowSelectionModel={selectedRows}
                            components={{
                              Toolbar: () => <GridToolbar />,
                              ExportToolbar: CustomExportToolbar,
                            }}
                            getRowId={(row) => row.id}
                          />

                          <TablePagination
                            rowsPerPageOptions={[5, 10, 20]}
                            component="div"
                            count={filteredUsers.length}
                            rowsPerPage={pageSize}
                            page={page}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsPerPageChange}
                          />

                          {selectedRows.length > 0 && (
                            <Button
                              variant="contained"
                              color="error"
                              onClick={handleDeleteSelected}
                              style={{
                                marginLeft: "30px",
                                marginTop: "10px",
                                marginBottom: "30px",
                              }}
                            >
                              Delete Selected Lectures
                            </Button>
                          )}
                        </>
                      )}
                    </>
                  </Stack>
                )}

                {/* Subadmin list (kept as-is structurally, audio fix applies in grid above) */}
                {role === "subadmin" && (
                  <Stack sx={{ width: "100%", borderRadius: "10px" }}>
                    {users?.length ? (
                      users.map((item, index) => {
                        const date = item?.starting_date?.split?.(" ")?.[0] || "";
                        const today = new Date();
                        const dd = String(today.getDate()).padStart(2, "0");
                        const mm = String(today.getMonth() + 1).padStart(2, "0");
                        const yyyy = today.getFullYear();
                        const todayDate = `${dd}-${mm}-${yyyy}`;
                        const golive = todayDate === date;

                        return (
                          <Box key={item?.id || index} sx={{ marginBottom: "25px", ...AccordionStyle }}>
                            <Box
                              pl={5}
                              pr={3}
                              pt={2}
                              pb={2}
                              onClick={() => setiid(item?.id)}
                              sx={{
                                cursor: "pointer",
                                width: "100%",
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                                position: "relative",
                              }}
                            >
                              <Stack spacing={0.5}>
                                <Typography color="#000000" sx={{ fontSize: "22px", fontWeight: "bold" }}>
                                  Lecture {index + 1} {item?.lecture_type === "YT" ? "( YouTube )" : ""}
                                </Typography>
                                <Typography sx={{ fontSize: "18px", fontWeight: "500" }}>
                                  Topic: {item?.title}
                                </Typography>
                                <Typography sx={{ fontSize: "18px", fontWeight: "500" }}>
                                  Subject: {item?.subject?.name || item?.subject}
                                </Typography>
                                <Typography sx={{ fontSize: "15px", fontWeight: "600" }}>
                                  Date:{" "}
                                  <span style={{ fontWeight: "400", color: "black" }}>
                                    {date || "NA"}
                                  </span>
                                </Typography>
                              </Stack>

                              {item?.lecture_type !== "YT" && (
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  sx={{ width: "25%", height: "100%" }}
                                >
                                  {item?.LiveOrRecorded === 1 && (
                                    <Button
                                      variant="contained"
                                      disabled={!golive}
                                      sx={{
                                        width: "60%",
                                        alignSelf: "center",
                                        background: "#8080ff",
                                        color: "white",
                                        borderRadius: "20px",
                                        "&:hover": { background: "#0000ff" },
                                      }}
                                    >
                                      <Link
                                        to={
                                          item?.LiveOrRecorded === 1
                                            ? `/dashboard/livestream/${item?.title}/${item?.id}/${Id}/${item?.description}`
                                            : "/dashboard/app"
                                        }
                                        style={{ color: "white", textDecoration: "none" }}
                                      >
                                        {item?.LiveOrRecorded === 1 ? "Go Live" : "View"}
                                      </Link>
                                    </Button>
                                  )}
                                  {item?.LiveOrRecorded === 2 && (
                                    <a
                                      href={item?.material?.fileLoc?.replace(/\\\\/g, "")}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ width: "60%", color: "white", textDecoration: "none" }}
                                    >
                                      <Button
                                        variant="contained"
                                        sx={{
                                          width: "100%",
                                          alignSelf: "center",
                                          background: "#8080ff",
                                          color: "white",
                                          borderRadius: "20px",
                                          "&:hover": { background: "#0000ff" },
                                        }}
                                      >
                                        View
                                      </Button>
                                    </a>
                                  )}
                                </Stack>
                              )}

                              {item?.lecture_type === "YT" && (
                                <div className="button-align" style={{ width: "25%" }}>
                                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: "100%", height: "100%" }}>
                                    <a
                                      href={item?.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ width: "60%", color: "white", textDecoration: "none" }}
                                    >
                                      <Button
                                        variant="contained"
                                        disabled={!golive}
                                        sx={{
                                          width: "100%",
                                          alignSelf: "center",
                                          background: "#8080ff",
                                          color: "white",
                                          borderRadius: "20px",
                                          "&:hover": { background: "#0000ff" },
                                        }}
                                      >
                                        {item?.LiveOrRecorded === 2 ? "View" : "Go Live"}
                                      </Button>
                                    </a>
                                  </Stack>
                                </div>
                              )}
                            </Box>

                            <Collapse in={item?.id === iid} timeout="auto" unmountOnExit>
                              {item?.id === iid && (
                                <Stack
                                  pl={5}
                                  pr={5}
                                  spacing={1}
                                  sx={{
                                    bgcolor: "rgba(145, 158, 171, 0.24)",
                                    borderRadius: "0 0 18px 18px",
                                  }}
                                >
                                  <Typography>
                                    <span style={{ fontWeight: "700" }}>Description :</span>{" "}
                                    {(item?.description || "").replace(/(<([^>]+)>)/gi, "")}
                                  </Typography>
                                </Stack>
                              )}
                            </Collapse>

                            <Box sx={{ position: "absolute", top: "47px", right: "1%" }}>
                              <UserMoreMenu
                                title="BatchDetails"
                                lectId={item?.id}
                                id={item?.id}
                                req="delete-lecture"
                                batchId={Id}
                                setLoading={setLoading}
                              />
                            </Box>
                          </Box>
                        );
                      })
                    ) : (
                      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
                        <CircularProgress />
                      </Box>
                    )}
                  </Stack>
                )}

                {/* Teacher section (kept similar to your original; audio grid not used here) */}
                {role === "Teacher" && (
                  <Typography sx={{ ml: 3, mt: 2 }} color="text.secondary">
                    (Teacher view content not shown here; retained as per your original layout.)
                  </Typography>
                )}

                <ToastContainer
                  position="bottom-center"
                  autoClose={3000}
                  hideProgressBar
                  newestOnTop={false}
                  closeOnClick
                  rtl={false}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                  theme="light"
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={12} md={4}>
              <BatchDetailsCard batchId={Id} setBatchName={setBatchName} />
            </Grid>
          </Grid>
        </Page>
      )}
    </>
  );
};

export default BatchDetails;
